import uniqBy from 'lodash/uniqBy';
import classNames from 'classnames';
import React, { useEffect, useMemo, useRef } from 'react';

import config from 'config';

import {
  ALLOCATION,
  TERMINATION_RESTRICTION_INFO,
  TERMINATION_RESTRICTION_APPRAISER_MESSAGE,
  TERMINATION_RESTRICTION_OFFBOARDING_MESSAGE,
  TERMINATION_RESTRICTION_LEAVE_ISSUER_MESSAGE,
} from 'constants/appConstants';
import { ALL } from 'constants/fetchTypes';
import { MY_TEAM_FILTERS } from 'constants/myTeam';
import { USER_ATTENDANCE } from 'constants/routes';
import { PROJECT_DETAILS_URL } from 'constants/endpoints';
import { ATTENDANCE_SUMMARY_FIELDS } from 'constants/attendanceStatus';

import Info from 'components/common/inputWrapper/components/Info';

import { interpolate, pluralize } from 'utils/string';
import { handleError } from 'utils/errorHandler';

import { useFetchUserTeam } from 'hooks/query/useFetchUserTeam';
import { useFetchUserAllocations } from 'hooks/query/useFetchUserAllocations';
import { useFetchAttendanceSummary } from 'hooks/query/useFetchAttendanceSummary';

interface TerminationRestrictionInfoProps {
  userFirstName: string;
  userId: number | string;
  setIsNotAllowedToTerminate: React.Dispatch<React.SetStateAction<boolean>>;
  isScrollable?: boolean;
}

interface AttendanceSummaryData {
  leave?: { requested?: number };
  worklog?: { submitted?: number };
}

interface UserAllocation {
  project?: { id?: number; name?: string };
}

interface AppraiseeItem {
  relationInProject?: { id?: number; name?: string };
  currentAllocations: Array<{ type: string; appraiserId: number; name: string; areaId: number }>;
  user: { id: number; fullname: string };
}

const TerminationRestrictionInfo: React.FC<TerminationRestrictionInfoProps> = ({
  userFirstName,
  userId,
  setIsNotAllowedToTerminate,
  isScrollable,
}) => {
  const {
    data: appraisees,
    isLoading: isAppraiseesLoading,
    isError: isAppraiseesError,
    error: appraiseesError,
    errorUpdatedAt: appraiseesErrorUpdatedAt,
  } = useFetchUserTeam<AppraiseeItem>(userId, {
    relations: MY_TEAM_FILTERS.APPRAISER,
    includeFutureAllocation: true,
  });

  const {
    data: allocations,
    isLoading: isAllocationsLoading,
    isError: isAllocationsError,
    error: allocationsError,
    errorUpdatedAt: allocationsErrorUpdatedAt,
  } = useFetchUserAllocations({
    userId,
    isLocked: false,
  });

  const {
    data: summaryData,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
    error: summaryError,
    errorUpdatedAt: summaryErrorUpdatedAt,
  } = useFetchAttendanceSummary({
    fetchType: ALL,
    leaveIssuerId: userId,
    fields: [ATTENDANCE_SUMMARY_FIELDS.WORKLOG, ATTENDANCE_SUMMARY_FIELDS.LEAVE].join(','),
  });
  const lastHandledAppraiseesErrorAtRef = useRef(0);
  const lastHandledAllocationsErrorAtRef = useRef(0);
  const lastHandledSummaryErrorAtRef = useRef(0);

  useEffect(() => {
    if (!isAppraiseesError || !appraiseesError || appraiseesErrorUpdatedAt <= lastHandledAppraiseesErrorAtRef.current) {
      return;
    }

    handleError(appraiseesError as object);
    lastHandledAppraiseesErrorAtRef.current = appraiseesErrorUpdatedAt;
  }, [isAppraiseesError, appraiseesError, appraiseesErrorUpdatedAt]);

  useEffect(() => {
    if (
      !isAllocationsError ||
      !allocationsError ||
      allocationsErrorUpdatedAt <= lastHandledAllocationsErrorAtRef.current
    ) {
      return;
    }

    handleError(allocationsError as object);
    lastHandledAllocationsErrorAtRef.current = allocationsErrorUpdatedAt;
  }, [isAllocationsError, allocationsError, allocationsErrorUpdatedAt]);

  useEffect(() => {
    if (!isSummaryError || !summaryError || summaryErrorUpdatedAt <= lastHandledSummaryErrorAtRef.current) {
      return;
    }

    handleError(summaryError as object);
    lastHandledSummaryErrorAtRef.current = summaryErrorUpdatedAt;
  }, [isSummaryError, summaryError, summaryErrorUpdatedAt]);

  const issuerPendingActions = useMemo(() => {
    if (!summaryData) {
      return;
    }

    return {
      pendingLeaveActions: (summaryData.data as AttendanceSummaryData).leave?.requested || 0,
      pendingWorklogActions: (summaryData.data as AttendanceSummaryData).worklog?.submitted || 0,
    };
  }, [summaryData]);

  const allocationItems = useMemo<UserAllocation[]>(
    () => (Array.isArray(allocations) ? (allocations as UserAllocation[]) : []),
    [allocations]
  );

  useEffect(() => {
    if (
      isAppraiseesLoading ||
      isAllocationsLoading ||
      isSummaryLoading ||
      !allocations ||
      !appraisees ||
      !issuerPendingActions
    ) {
      return setIsNotAllowedToTerminate(true);
    }

    const projectAllocations = allocationItems.filter(({ project }) => project?.id);

    const isUserAppraiser = Boolean(appraisees.length);
    const isAllocatedInProject = Boolean(projectAllocations.length);
    const hasPendingActions = Boolean(
      issuerPendingActions.pendingLeaveActions || issuerPendingActions.pendingWorklogActions
    );

    setIsNotAllowedToTerminate(isUserAppraiser || isAllocatedInProject || hasPendingActions);
  }, [
    allocations,
    appraisees,
    isAllocationsLoading,
    isAppraiseesLoading,
    isSummaryLoading,
    issuerPendingActions,
    allocationItems,
    setIsNotAllowedToTerminate,
  ]);
  const projectAllocations = uniqBy(
    allocationItems.filter(({ project }) => project?.id),
    'project.id'
  );

  const uniqueAppraisersCount = useMemo(() => {
    if (!appraisees) {
      return;
    }

    return uniqBy(appraisees, 'user.id').length;
  }, [appraisees]);

  const groupedAppraisees = useMemo(() => {
    if (!appraisees) {
      return;
    }

    return appraisees.reduce<
      Record<string, { projectOrArea: { id: number; name: string }; appraisees: AppraiseeItem[] }>
    >((acc, curr) => {
      const { relationInProject, currentAllocations, user } = curr;

      // Handle project-based appraisees
      if (relationInProject?.id) {
        const projectKey = `${relationInProject.name}-${relationInProject.id}`;

        if (!acc[projectKey]) {
          acc[projectKey] = {
            projectOrArea: {
              id: relationInProject.id,
              name: relationInProject.name,
            },
            appraisees: [],
          };
        }
        acc[projectKey].appraisees.push(curr);
      }

      // Handle area-based appraisees
      currentAllocations.forEach(({ type, appraiserId, name, areaId }) => {
        if (type === ALLOCATION.AREA && appraiserId === Number(userId)) {
          const areaKey = `${name}-${areaId}`;

          if (!acc[areaKey]) {
            acc[areaKey] = {
              projectOrArea: {
                id: areaId,
                name,
              },
              appraisees: [],
            };
          }

          const isAlreadyAdded = acc[areaKey].appraisees.some(
            ({ user: appraiseeUser }) => appraiseeUser.id === user.id
          );

          if (!isAlreadyAdded) {
            acc[areaKey].appraisees.push(curr);
          }
        }
      });

      return acc;
    }, {});
  }, [appraisees, userId]);

  if (!projectAllocations || !appraisees || !issuerPendingActions) {
    return null;
  }

  const { pendingLeaveActions, pendingWorklogActions } = issuerPendingActions;

  if (!projectAllocations.length && !appraisees.length && !pendingLeaveActions && !pendingWorklogActions) {
    return null;
  }

  const getPendingActionMessage = () => {
    if (pendingLeaveActions && pendingWorklogActions) {
      return `${pendingLeaveActions} ${pluralize(
        'leave request',
        pendingLeaveActions
      )} and ${pendingWorklogActions} ${pluralize('worklog', pendingWorklogActions)}`;
    }

    if (pendingLeaveActions) {
      return `${pendingLeaveActions} ${pluralize('leave request', pendingLeaveActions)}`;
    }

    if (pendingWorklogActions) {
      return `${pendingWorklogActions} ${pluralize('worklog', pendingWorklogActions)}`;
    }
  };

  const getMessage = () => {
    return (
      <div
        className={classNames('termination-restriction', {
          'termination-restriction--scrollable': isScrollable,
        })}
      >
        <p>
          {interpolate(TERMINATION_RESTRICTION_INFO, {
            user: userFirstName,
          })}
        </p>

        <ul>
          {!!projectAllocations.length && (
            <div>
              <li>
                <p className="font-weight-semi-bold">
                  {interpolate(TERMINATION_RESTRICTION_OFFBOARDING_MESSAGE, {
                    user: userFirstName,
                    projectCount: `${projectAllocations.length} ${pluralize('project', projectAllocations.length)}`,
                  })}
                </p>
                <p>
                  {projectAllocations.map((allocation, index) => (
                    <React.Fragment key={allocation.project?.id || `${allocation.project?.name}-${index}`}>
                      {index > 0 && ', '}
                      <a
                        href={interpolate(PROJECT_DETAILS_URL, {
                          id: allocation.project?.id,
                        })}
                      >
                        {allocation.project?.name}
                      </a>
                    </React.Fragment>
                  ))}
                </p>
              </li>
            </div>
          )}

          {!!appraisees.length && (
            <div>
              <li>
                <p className="font-weight-semi-bold">
                  {interpolate(TERMINATION_RESTRICTION_APPRAISER_MESSAGE, {
                    user: userFirstName,
                    teamMemberCount: `${uniqueAppraisersCount} ${pluralize('team member', uniqueAppraisersCount)}`,
                  })}
                </p>
                {Object.keys(groupedAppraisees).map((key) => {
                  const { projectOrArea, appraisees } = groupedAppraisees[key];

                  return (
                    <p key={key}>
                      {projectOrArea.name}:&nbsp;
                      {appraisees.map((appraisee, index) => (
                        <React.Fragment key={`${key}-${appraisee.user.id}`}>
                          {index > 0 && ', '}
                          <a
                            href={interpolate(config.endpoints.lms.userProfile, {
                              id: appraisee.user.id,
                            })}
                          >
                            {appraisee.user.fullname}
                          </a>
                        </React.Fragment>
                      ))}
                    </p>
                  );
                })}
              </li>
            </div>
          )}

          {(!!pendingLeaveActions || !!pendingWorklogActions) && (
            <div>
              <li>
                <p className="font-weight-semi-bold">
                  {interpolate(TERMINATION_RESTRICTION_LEAVE_ISSUER_MESSAGE, {
                    user: userFirstName,
                    actions: getPendingActionMessage(),
                  })}
                </p>
                <a
                  href={interpolate(USER_ATTENDANCE, {
                    userId,
                  })}
                >
                  View attendance
                </a>
              </li>
            </div>
          )}
        </ul>
      </div>
    );
  };

  return (
    <Info
      shouldAlert
      message={getMessage()}
      className={classNames('termination-restriction-info', { 'mt-0': isScrollable })}
    />
  );
};

export default TerminationRestrictionInfo;
